import type { NatalChartData, NatalPlanetPlacement } from '@/api/client';

/**
 * SVG natal-chart wheel, drawn from real ecliptic positions.
 *
 * Convention: Ascendant at 9 o'clock (left), zodiac signs progress
 * counter-clockwise from there — the standard Western chart layout.
 * Mapping: screen_angle_cw_deg = (180 - eclipticLon + asc) mod 360.
 *
 * Rings (outer→inner):
 *   1. zodiac signs (12 segments × 30°)
 *   2. house cusps (Placidus, unequal segments)
 *   3. planets — small glyph chips on a fixed radius
 *   4. aspect lines — chords drawn between aspected planets, colour-coded
 *
 * Why 320×320 default: comfortable on a 360-wide mobile screen with
 * gutters; readable glyphs at this resolution; scales cleanly to larger
 * sizes via the `size` prop without re-laying anything out.
 */

const SIGN_GLYPHS = [
  '♈', '♉', '♊', '♋', '♌', '♍',
  '♎', '♏', '♐', '♑', '♒', '♓',
];

const PLANET_GLYPHS: Record<NatalPlanetPlacement['planet'], string> = {
  sun: '☉', moon: '☽', mercury: '☿', venus: '♀', mars: '♂',
  jupiter: '♃', saturn: '♄', uranus: '♅', neptune: '♆', pluto: '♇',
};

// Harmonious aspects (trine/sextile) blue, hard aspects (square/opposition)
// reddish, conjunction neutral gold. Dashed = "soft" energy of the aspect.
const ASPECT_COLOURS = {
  conjunction: '#f0d58e',
  trine: '#86c5e0',
  sextile: '#86c5e0',
  square: '#e08a86',
  opposition: '#e08a86',
} as const;

const ASPECT_DASHES = {
  conjunction: undefined,
  trine: undefined,
  sextile: '4 3',
  square: undefined,
  opposition: '6 3',
} as const;

function eclipticToAngle(lon: number, ascLon: number): number {
  return (((180 - lon + ascLon) % 360) + 360) % 360;
}

function point(r: number, deg: number): { x: number; y: number } {
  const rad = (deg * Math.PI) / 180;
  return { x: r * Math.cos(rad), y: r * Math.sin(rad) };
}

// Deterministic pseudo-random — same chart input always produces the
// same star pattern. Important for two reasons:
//   1) React re-renders the wheel any time props change; if we use
//      Math.random() the stars would shift on every re-render, looking
//      buggy/glitchy.
//   2) PDF re-render (if we ever screenshot the wheel) must match the
//      on-screen wheel. Same seed → same image.
// Algorithm: linear congruential generator with stable constants.
function makeRng(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
}

interface Star { x: number; y: number; r: number; opacity: number; }

function generateStars(count: number, maxRadius: number): Star[] {
  const rng = makeRng(12345);
  const stars: Star[] = [];
  while (stars.length < count) {
    // Uniform area distribution = sqrt random for r. Otherwise all
    // stars cluster near the centre because polar coords aren't uniform.
    const r = Math.sqrt(rng()) * maxRadius;
    const angle = rng() * 360;
    const rad = (angle * Math.PI) / 180;
    stars.push({
      x: r * Math.cos(rad),
      y: r * Math.sin(rad),
      // Bumped from 0.4-1.5 / 0.25-0.80 — at the smaller sizes the stars
      // were essentially invisible on mobile Telegram WebView with its
      // sub-pixel rounding.
      r: 0.7 + rng() * 1.3,           // 0.7–2.0 px
      opacity: 0.45 + rng() * 0.45,   // 0.45–0.90
    });
  }
  return stars;
}

const BACKGROUND_STARS = generateStars(55, 148);

interface Props {
  chart: NatalChartData;
  size?: number;
}

export default function NatalWheel({ chart, size = 320 }: Props) {
  // Radii — designed so 12 sign glyphs + 10 planets + asp lines all fit
  // without crowding at 320px. Re-scaling via `size` keeps proportions.
  const R_OUTER = 150;
  const R_SIGN_IN = 120;
  const R_HOUSE_IN = 85;
  const R_PLANET = 70;
  const R_ASPECT = 60;

  const ascLon = chart.ascendant;

  // ── Sign ring ────────────────────────────────────────────────────────
  const signRadials: JSX.Element[] = [];
  const signGlyphs: JSX.Element[] = [];
  for (let i = 0; i < 12; i++) {
    const startLon = i * 30;
    const startAng = eclipticToAngle(startLon, ascLon);
    const pOut = point(R_OUTER, startAng);
    const pIn = point(R_SIGN_IN, startAng);
    signRadials.push(
      <line
        key={`sr-${i}`}
        x1={pIn.x} y1={pIn.y}
        x2={pOut.x} y2={pOut.y}
        stroke="#8b6f2e"
        strokeWidth={0.6}
      />,
    );
    const midAng = eclipticToAngle(startLon + 15, ascLon);
    const midR = (R_OUTER + R_SIGN_IN) / 2;
    const gp = point(midR, midAng);
    signGlyphs.push(
      <text
        key={`sg-${i}`}
        x={gp.x} y={gp.y}
        fill="#e8c356"
        fontSize={14}
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily="serif"
      >
        {SIGN_GLYPHS[i]}
      </text>,
    );
  }

  // ── House ring (Placidus — cusps are at the longitudes the backend
  // returned, NOT at uniform 30° intervals). Asc + IC + Desc + MC get
  // a brighter "axis" treatment so the chart's four cardinal points stay
  // obvious at a glance. ──
  const houseRadials: JSX.Element[] = [];
  const houseLabels: JSX.Element[] = [];
  for (let i = 0; i < 12; i++) {
    const cuspLon = chart.houses[i].longitude;
    const cuspAng = eclipticToAngle(cuspLon, ascLon);
    const pOut = point(R_SIGN_IN, cuspAng);
    const pIn = point(R_HOUSE_IN, cuspAng);
    // 1st/4th/7th/10th = Asc/IC/Desc/MC.
    const isAxis = i === 0 || i === 3 || i === 6 || i === 9;
    houseRadials.push(
      <line
        key={`hr-${i}`}
        x1={pIn.x} y1={pIn.y}
        x2={pOut.x} y2={pOut.y}
        stroke={isAxis ? '#e8c356' : '#5d4818'}
        strokeWidth={isAxis ? 1.0 : 0.4}
      />,
    );
    // House-number label midway between this cusp and the next.
    const nextCuspLon = chart.houses[(i + 1) % 12].longitude;
    // Careful with the 360° wrap: if next < this, the segment spans the
    // 360→0 boundary and the simple mean would put the label on the
    // wrong side of the wheel.
    let midLon = (cuspLon + nextCuspLon) / 2;
    if (nextCuspLon < cuspLon) {
      midLon = (((cuspLon + nextCuspLon + 360) / 2) % 360);
    }
    const midAng = eclipticToAngle(midLon, ascLon);
    const lp = point((R_SIGN_IN + R_HOUSE_IN) / 2, midAng);
    houseLabels.push(
      <text
        key={`hl-${i}`}
        x={lp.x} y={lp.y}
        fill="#8b6f2e"
        fontSize={9}
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily="sans-serif"
      >
        {i + 1}
      </text>,
    );
  }

  // ── Aspect lines — chords between aspected planets ─────────────────
  const aspectLines: JSX.Element[] = [];
  for (const a of chart.aspects) {
    const pa = chart.planets.find((p) => p.planet === a.planet_a);
    const pb = chart.planets.find((p) => p.planet === a.planet_b);
    if (!pa || !pb) continue;
    const aAng = eclipticToAngle(pa.longitude, ascLon);
    const bAng = eclipticToAngle(pb.longitude, ascLon);
    const aPos = point(R_ASPECT, aAng);
    const bPos = point(R_ASPECT, bAng);
    aspectLines.push(
      <line
        key={`as-${a.planet_a}-${a.planet_b}-${a.aspect}`}
        x1={aPos.x} y1={aPos.y}
        x2={bPos.x} y2={bPos.y}
        stroke={ASPECT_COLOURS[a.aspect]}
        strokeWidth={0.6}
        strokeDasharray={ASPECT_DASHES[a.aspect]}
        opacity={0.55}
      />,
    );
  }

  // ── Planet glyphs (collision-aware) ────────────────────────────────
  // Stelliums (multiple planets within ~14°) would otherwise stack on
  // top of each other and hide all but the last-rendered one. Group
  // adjacent planets and stagger them along the radial axis so every
  // glyph is independently visible.
  //
  // The clustering threshold (14°) is roughly the angular width of one
  // glyph at R_PLANET=70 (where 1° ≈ 1.22 px and the glyph diameter is
  // 18 px → ~15° of overlap risk). 14° catches the worst cases without
  // false-positives for planets that are merely "close".
  const OVERLAP_THRESHOLD_DEG = 14;
  const RADIAL_BAND = 20;  // total px range planets in one cluster occupy

  // Pair each planet with its screen angle so we sort by visual position,
  // not raw longitude. Identical math but easier to reason about.
  const planetsWithAngles = chart.planets.map((p) => ({
    p,
    ang: eclipticToAngle(p.longitude, ascLon),
  }));

  // Cluster by ANGULAR proximity, walking around the circle counter-
  // clockwise (= longitude ascending). Wrap-around: after the linear
  // pass we check whether the first and last clusters should merge.
  const sortedByAngle = [...planetsWithAngles].sort((a, b) => b.ang - a.ang);
  // ^ DESCENDING screen angle == ASCENDING ecliptic longitude (because of
  //   our 180 - lon mapping). Sorting this way puts adjacent planets in
  //   adjacent positions in the array.

  const clusters: (typeof planetsWithAngles)[] = [];
  for (const item of sortedByAngle) {
    if (clusters.length === 0) {
      clusters.push([item]);
      continue;
    }
    const lastCluster = clusters[clusters.length - 1];
    const lastItem = lastCluster[lastCluster.length - 1];
    let diff = Math.abs(item.ang - lastItem.ang);
    if (diff > 180) diff = 360 - diff;
    if (diff < OVERLAP_THRESHOLD_DEG) {
      lastCluster.push(item);
    } else {
      clusters.push([item]);
    }
  }
  // Wrap-around merge: if the last cluster's tail meets the first
  // cluster's head across the 0°/360° seam, fuse them.
  if (clusters.length > 1) {
    const first = clusters[0][0];
    const last = clusters[clusters.length - 1][clusters[clusters.length - 1].length - 1];
    let diff = Math.abs(first.ang - last.ang);
    if (diff > 180) diff = 360 - diff;
    if (diff < OVERLAP_THRESHOLD_DEG) {
      clusters[0] = [...clusters.pop()!, ...clusters[0]];
    }
  }

  // For each cluster of size N, compute per-planet rendering radius.
  // Solo planets stay on the base ring. Multi-planet clusters spread
  // evenly across a 20px radial band centred on R_PLANET — inner-most
  // planet closer to centre, outer-most closer to the house ring.
  const planetGlyphs: JSX.Element[] = [];
  for (const cluster of clusters) {
    for (let i = 0; i < cluster.length; i++) {
      const { p, ang } = cluster[i];
      let r = R_PLANET;
      if (cluster.length > 1) {
        const step = RADIAL_BAND / (cluster.length - 1);
        r = R_PLANET - RADIAL_BAND / 2 + i * step;
      }
      const pos = point(r, ang);
      planetGlyphs.push(
        <g key={`pl-${p.planet}`}>
          <circle
            cx={pos.x} cy={pos.y} r={9}
            fill="#0a0e22"
            stroke="#3a2e10"
            strokeWidth={0.5}
          />
          <text
            x={pos.x} y={pos.y}
            fill="#e8c356"
            fontSize={12}
            textAnchor="middle"
            dominantBaseline="central"
            fontFamily="serif"
          >
            {PLANET_GLYPHS[p.planet]}
          </text>
          {p.retrograde && (
            <text
              x={pos.x + 8} y={pos.y - 6}
              fill="#e08a86"
              fontSize={6}
              textAnchor="start"
              dominantBaseline="central"
            >
              R
            </text>
          )}
        </g>,
      );
    }
  }

  // ── Asc / MC labels just outside the outer ring ───────────────────
  const ascAng = eclipticToAngle(chart.ascendant, ascLon);   // always 180°
  const mcAng = eclipticToAngle(chart.midheaven, ascLon);
  const ascLabelPos = point(R_OUTER + 10, ascAng);
  const mcLabelPos = point(R_OUTER + 10, mcAng);

  return (
    <svg
      // viewBox is 10px larger than R_OUTER each side so the ASC/MC text
      // labels (which sit at R_OUTER + 10 to clear the ring) aren't
      // clipped by the SVG boundary. The wheel itself stays the same
      // visual size — only the padding margin around it grows.
      viewBox="-170 -170 340 340"
      width={size}
      height={size}
      role="img"
      aria-label="Natal chart wheel"
    >
      <defs>
        <radialGradient id="natalBg" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#1a2049" />
          <stop offset="100%" stopColor="#0a0e22" />
        </radialGradient>
        {/* Soft gold glow at the very centre — gives the chart a 'lit
            from within' feel without competing with the planet glyphs.
            Sits behind the centre aspect circle. */}
        <radialGradient id="natalCenter" cx="50%" cy="50%" r="50%">
          <stop offset="0%"  stopColor="#e8c356" stopOpacity={0.35} />
          <stop offset="60%" stopColor="#e8c356" stopOpacity={0.06} />
          <stop offset="100%" stopColor="#e8c356" stopOpacity={0} />
        </radialGradient>
      </defs>

      {/* Background disc */}
      <circle cx={0} cy={0} r={R_OUTER} fill="url(#natalBg)" />

      {/* Star particles scattered in the background — pure decoration,
          no semantic meaning. Same pattern every time (see makeRng). */}
      <g aria-hidden="true">
        {BACKGROUND_STARS.map((s, i) => (
          <circle
            key={`st-${i}`}
            cx={s.x} cy={s.y} r={s.r}
            fill="#f0e6c4"
            opacity={s.opacity}
          />
        ))}
      </g>

      {/* Central gold glow */}
      <circle cx={0} cy={0} r={42} fill="url(#natalCenter)" />

      {/* Concentric rings */}
      <circle cx={0} cy={0} r={R_OUTER}   fill="none" stroke="#8b6f2e" strokeWidth={1.0} />
      <circle cx={0} cy={0} r={R_OUTER - 2.5} fill="none" stroke="#8b6f2e" strokeWidth={0.4} opacity={0.6} />
      <circle cx={0} cy={0} r={R_SIGN_IN} fill="none" stroke="#8b6f2e" strokeWidth={0.8} />
      <circle cx={0} cy={0} r={R_HOUSE_IN} fill="none" stroke="#5d4818" strokeWidth={0.5} />
      <circle cx={0} cy={0} r={R_ASPECT}  fill="none" stroke="#3a2e10" strokeWidth={0.4} />

      {/* Four small decorative stars at the cardinal screen positions
          (just outside the outer ring). Subtle marker that this is an
          astrological object without overloading the central wheel. */}
      {[0, 90, 180, 270].map((deg) => {
        const p = point(R_OUTER + 14, deg);
        return (
          <g
            key={`orn-${deg}`}
            transform={`translate(${p.x}, ${p.y})`}
            opacity={0.55}
          >
            <path
              d="M 0 -4 L 1 -1 L 4 0 L 1 1 L 0 4 L -1 1 L -4 0 L -1 -1 Z"
              fill="#e8c356"
            />
          </g>
        );
      })}

      {/* Sign ring */}
      {signRadials}
      {signGlyphs}

      {/* House ring */}
      {houseRadials}
      {houseLabels}

      {/* Aspect lines (drawn before planets so glyphs sit on top) */}
      {aspectLines}

      {/* Planet glyphs */}
      {planetGlyphs}

      {/* Asc / MC labels */}
      <text
        x={ascLabelPos.x} y={ascLabelPos.y}
        fill="#e8c356"
        fontSize={9}
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily="sans-serif"
        fontWeight="bold"
      >
        ASC
      </text>
      <text
        x={mcLabelPos.x} y={mcLabelPos.y}
        fill="#e8c356"
        fontSize={9}
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily="sans-serif"
        fontWeight="bold"
      >
        MC
      </text>
    </svg>
  );
}
